/**
 * Rutas y textos compartidos del embudo membresía → cuenta → aprobación → acceso.
 * Mantiene coherencia entre portal, registro, pendiente y mi-acceso.
 *
 * Orden del embudo (post-pago):
 *   PORTAL_REGISTRO → PORTAL_COMPLETAR_REGISTRO → PENDING_APPROVAL → MI_ACCESO
 * ENTRAR = sesión existente sin alta de membresía en curso.
 *
 * MI_ACCESO canónico vive bajo /portal/mi-acceso (link de acceso asignado).
 * /mi-acceso redirige al canónico por compatibilidad.
 */

export const MEMBERSHIP_ACCESS_PORTAL_PATH = "/portal-acceso";

/**
 * Portal del miembro logueado. Destino del alta (sin sala de espera) y del
 * fallback de `ProtectedAdminRoute` para usuarios sin rol admin.
 */
export const USER_PORTAL_PATH = "/portal";

/** Rutas del embudo público (única fuente para Router y redirects). */
export const MEMBERSHIP_ROUTE = {
  REGISTRO: "/registro",
  /** Alias canónico post-pago: misma vista que REGISTRO pero bajo la URL del portal. */
  PORTAL_REGISTRO: "/portal/registro",
  COMPLETAR_REGISTRO: "/completar-registro",
  /** Alias portal del paso de perfil post-Clerk (misma vista que COMPLETAR_REGISTRO). */
  PORTAL_COMPLETAR_REGISTRO: "/portal/completar-registro",
  PENDING_APPROVAL: "/pending-approval",
  /**
   * Link de acceso asignado (Telegram, etc.).
   * Canónico bajo el portal: el fan se queda en /portal/* con su link.
   */
  MI_ACCESO: "/portal/mi-acceso",
  /** Legacy path — App redirige a MI_ACCESO. */
  MI_ACCESO_LEGACY: "/mi-acceso",
  ENTRAR: "/entrar",
} as const;

export type MembershipFunnelPath = (typeof MEMBERSHIP_ROUTE)[keyof typeof MEMBERSHIP_ROUTE];

/** Texto del enlace secundario repetido en las pantallas del embudo. */
export const MEMBERSHIP_PORTAL_LINK_TEXT = "Guía de pasos de acceso";

/**
 * Destino canónico del CTA **free** en cualquier país/template: Clerk `/registro`.
 *
 * No abrir Telegram directo desde landings — el free tier requiere cuenta +
 * pending approval; el link de TG se puede pasar como query para post-aprobación.
 */
export type FreeClerkRegistroSource =
  | "dual_cta"
  | "bio_simple"
  | "reels_end"
  | "premium_invite"
  | "mux_overlay"
  | "other";

export function buildFreeClerkRegistroUrl(opts?: {
  /** Invite t.me opcional — se guarda en query para el embudo post-aprobación. */
  freeTelegramUrl?: string | null;
  email?: string | null;
  /** Superficie de origen (telemetría / recovery). */
  source?: FreeClerkRegistroSource;
  /** Query extra (sin pisar plan_tier / access_request). */
  extraParams?: Record<string, string | undefined | null>;
}): string {
  const q = new URLSearchParams();
  q.set("plan_tier", "free");
  q.set("access_request", "1");

  const source = opts?.source ?? "other";
  if (source === "dual_cta") q.set("from_dual_cta", "1");
  else if (source === "bio_simple") q.set("from_bio_simple", "1");
  else if (source === "reels_end") q.set("from_reels_end", "1");
  else if (source === "premium_invite") q.set("from_premium_invite", "1");
  else if (source === "mux_overlay") q.set("from_mux_overlay", "1");
  else q.set("from_free_cta", "1");

  const tg = (opts?.freeTelegramUrl ?? "").trim();
  if (tg && /^https?:\/\//i.test(tg)) {
    try {
      const host = new URL(tg).hostname.toLowerCase();
      if (
        host === "t.me" ||
        host === "telegram.me" ||
        host === "telegram.dog" ||
        host.endsWith(".t.me")
      ) {
        q.set("free_telegram_url", tg);
      }
    } catch {
      /* ignore invalid */
    }
  }

  const em = (opts?.email ?? "").trim().toLowerCase();
  if (em.includes("@")) q.set("email", em);

  if (opts?.extraParams) {
    for (const [k, v] of Object.entries(opts.extraParams)) {
      if (!k || v == null || v === "") continue;
      if (k === "plan_tier" || k === "access_request") continue;
      q.set(k, v);
    }
  }

  return `${MEMBERSHIP_ROUTE.REGISTRO}?${q.toString()}`;
}

